import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { brand, statusTone } from '../../theme';

interface UsageMeterProps {
  label: string;
  used: number;
  /** -1 (or any negative) means unlimited: shown as text, with no bar. */
  limit: number;
  /** How to write the numbers, e.g. formatBytes for storage. Defaults to plain counts. */
  format?: (value: number) => string;
  /** What happens next, shown from 80% used (e.g. "New uploads stop at 1 GB."). */
  nearLimitHint?: React.ReactNode;
}

/**
 * "0.4 GB of 1 GB". Neutral below 80%, amber from 80%, red at 100%.
 */
const UsageMeter: React.FC<UsageMeterProps> = ({ label, used, limit, format = (v) => String(v), nearLimitHint }) => {
  const unlimited = !Number.isFinite(limit) || limit < 0;
  const ratio = unlimited || limit === 0 ? 0 : used / limit;
  const percent = Math.min(100, Math.max(0, Math.round(ratio * 100)));
  const barColor = ratio >= 1 ? statusTone.problem.main : ratio >= 0.8 ? statusTone.attention.main : brand.slate;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {unlimited ? `${format(used)} · Unlimited` : `${format(used)} of ${format(limit)}`}
        </Typography>
      </Box>
      {!unlimited && (
        <LinearProgress
          variant="determinate"
          value={percent}
          aria-label={`${label}: ${format(used)} of ${format(limit)}`}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: barColor },
          }}
        />
      )}
      {!unlimited && ratio >= 0.8 && nearLimitHint && (
        <Typography variant="body2" sx={{ mt: 0.75, color: ratio >= 1 ? statusTone.problem.fg : statusTone.attention.fg }}>
          {nearLimitHint}
        </Typography>
      )}
    </Box>
  );
};

export default UsageMeter;
